"use client"

import { useState, useEffect } from "react"
import { Card } from "@/components/UI/card"
import { Button } from "@/components/UI/button"
import { Input } from "@/components/UI/input"
import { Label } from "@/components/UI/label"
import { ChevronDown, ChevronUp, Plus, X, Tag } from "lucide-react"

interface MetaField {
  setting_id: number;
  value: string;
}

interface MetaFieldsProps {
  value?: MetaField[];
  onChange?: (fields: MetaField[]) => void;
  editingData?: any;
}

export function MetaFields({ value = [], onChange, editingData }: MetaFieldsProps) {
  const [isExpanded, setIsExpanded] = useState(true) // Đặt mặc định là true để dễ test
  const [metaFields, setMetaFields] = useState<MetaField[]>(value)


  // Sync with external value prop
  useEffect(() => {
    setMetaFields(value)
  }, [value])

  // Load meta fields from editing data when editing
  useEffect(() => {
    if (editingData?.meta_fields && Array.isArray(editingData.meta_fields)) {
      const formattedFields = editingData.meta_fields.map((field: any) => ({
        setting_id: field.setting_id || field.id || 1,
        value: field.value || ''
      }))
      setMetaFields(formattedFields)
      // Notify parent component
      if (onChange) {
        onChange(formattedFields)
      }
    }
  }, [editingData, onChange])

  const addMetaField = () => {
    const newFields = [...metaFields, { setting_id: 1, value: "" }]
    setMetaFields(newFields)
    if (onChange) {
      onChange(newFields)
    }
  }

  const removeMetaField = (index: number) => {
    const newFields = metaFields.filter((_, i) => i !== index)
    setMetaFields(newFields)
    if (onChange) {
      onChange(newFields)
    }
  }

  const updateMetaField = (index: number, field: "setting_id" | "value", value: string | number) => {
    const newFields = [...metaFields]
    if (field === "setting_id") {
      newFields[index][field] = Number(value)
    } else {
      newFields[index][field] = String(value)
    }
    setMetaFields(newFields)
    if (onChange) {
      onChange(newFields)
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between group"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-secondary">
            <Tag className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-foreground">
              Thông tin Meta <span className="text-sm font-normal text-muted-foreground">(Tùy chọn)</span>
            </h3>
            <p className="text-sm text-muted-foreground">Thêm các thông tin meta bổ sung cho link</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-6 space-y-4">
          {metaFields.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
              <Tag className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-4">Chưa có thông tin meta nào</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addMetaField}
                className="border-border bg-transparent"
              >
                <Plus className="w-4 h-4 mr-2" />
                Thêm Meta Field
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {metaFields.map((field, index) => (
                  <div key={index} className="flex gap-3 p-4 rounded-lg bg-secondary/30 border border-border">
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Setting ID</Label>
                        <Input
                          type="number"
                          value={field.setting_id}
                          onChange={(e) => updateMetaField(index, "setting_id", e.target.value)}
                          placeholder="1"
                          min="1"
                          className="bg-input border-border text-foreground"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Value</Label>
                        <Input
                          value={field.value}
                          onChange={(e) => updateMetaField(index, "value", e.target.value)}
                          placeholder="New Homepage Title"
                          className="bg-input border-border text-foreground"
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeMetaField(index)}
                      className="mt-6 text-muted-foreground hover:text-destructive"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addMetaField}
                className="w-full border-border bg-transparent"
              >
                <Plus className="w-4 h-4 mr-2" />
                Thêm Meta Field
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
